<template>
	<div :class="['container', { 'hide-app': hideApp && !infoPage, 'container-small-padding': smallPadding }]">
		<div :class="['main-content', { loading: loading && !sendingMessage }]">
			<router-view />
		</div>
		<dialog-cookies v-if="showCookiesDialog" />
		<dialog-cookies-setting v-if="!showCookiesDialog && showCookiesSetting" />
		<dialog-install-prompt v-if="showInstallPrompt" />
		<log-in-modal v-if="showLoginModal" />
		<paywall-dialog v-if="showPaywall" :variant="paywallVariant" :topic-name="paywallTopicName" @close="closePaywall" />
		<limit-reached-dialog v-if="showLimitDialog" :variant="limitResource" @close="closeLimitDialog" />
		<subscription-suspended-dialog v-if="showSuspendedDialog" @close="closeSuspendedDialog" />
		<payment-dialog v-if="billingEnabled" />
		<dialog-component
			v-if="internetErrorDialog"
			title="Интернет-соединение нестабильно. Проверьте вашу сеть. Если всё в порядке, обновите страницу"
			firstBtnText="Обновить страницу"
			@close="closeInternetErrorDialog"
			@click-first-btn="closeInternetErrorDialog"
			:show-second-btn="false"
		/>
	</div>
</template>

<script setup lang="ts">
import { useCommonStore } from '@/stores/common'
import { useCookiesStore } from '@/stores/cookies'
import DialogCookies from '@/components/dialogs/DialogCookies.vue'
import DialogCookiesSetting from '@/components/dialogs/DialogCookiesSetting.vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import DialogInstallPrompt from '@/components/dialogs/DialogInstallPrompt.vue'
import LogInModal from '@/components/dialogs/LogInModal.vue'
import PaywallDialog from '@/components/paywall/PaywallDialog.vue'
import LimitReachedDialog from '@/components/paywall/LimitReachedDialog.vue'
import SubscriptionSuspendedDialog from '@/components/paywall/SubscriptionSuspendedDialog.vue'
import PaymentDialog from '@/components/billing/PaymentDialog.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaywallStore } from '@/stores/paywall'
import { useProfileStore } from '@/stores/profile'
import { usePlansStore } from '@/stores/plans'
import { usePwaStore } from '@/stores/pwa'
import { useUsageStore } from '@/stores/usage'
import { useBillingStore } from '@/stores/billing'
import { useGenerationStore } from '@/stores/generation'
import { computed, onBeforeMount, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppConfig from '@/config'

const commonStore = useCommonStore()
const cookiesStore = useCookiesStore()
const authStore = useAuthStore()
const paywallStore = usePaywallStore()
const profileStore = useProfileStore()
const plansStore = usePlansStore()
const pwaStore = usePwaStore()
const usageStore = useUsageStore()
const billingStore = useBillingStore()
const generationStore = useGenerationStore()

// Возврат сети: закрываем диалог «нет интернета» и добираем зависшие задачи генерации
// (по task_id проверяем, готов ли результат на сервере). См. generation store resumeAll().
function handleOnline() {
	commonStore.closeInternetErrorDialog()
	generationStore.resumeAll()
}

/**
 * Шторка несостоявшегося продления. Показываем её здесь, а не только из гейта: человек
 * должен узнать про сорванное списание сразу на входе, не дожидаясь попытки генерации.
 * Само решение — в сторе (syncSuspended), здесь остаётся только очерёдность на экране.
 *
 * Cookie-баннер держит собственный бэкдроп, и два бэкдропа сразу превращаются в кашу,
 * поэтому ждём его закрытия так же, как это делает PWA-приглашение (см. watch ниже).
 */
function maybeShowSuspended() {
	if (cookiesStore.showCookiesDialog) return
	paywallStore.syncSuspended()
}

/**
 * Возврат во вкладку. Срез /usage не перезапрашивается ни по таймеру, ни по фокусу, так
 * что человек, оплативший в другой вкладке или по ссылке из письма, видел бы шторку до
 * перезагрузки. Спрашиваем только пока продление считается сорванным — иначе это был бы
 * лишний запрос на каждое переключение вкладки.
 */
async function handleVisibilityChange() {
	if (document.hidden || !usageStore.isSuspended) return
	await usageStore.fetchUsage()
	maybeShowSuspended()
}

// Per-tenant <head>: index.html общий и статичный, поэтому правим title/description/
// theme-color/apple-touch-icon в рантайме из конфига тенанта.
document.title = AppConfig.pwaName
document.querySelector('meta[name="description"]')?.setAttribute('content', AppConfig.pwaName)
document.querySelector('meta[name="theme-color"]')?.setAttribute('content', AppConfig.pwaThemeColor)
document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute('href', AppConfig.pwaAppleIcon)
if (AppConfig.isMts) {
	document.querySelector('link[rel="icon"]')?.setAttribute('href', AppConfig.pwaFavicon)
	document.querySelector('link[rel="shortcut icon"]')?.setAttribute('href', AppConfig.pwaFavicon)
}

authStore.loginMock()

function closeInternetErrorDialog() {
	commonStore.closeInternetErrorDialog()
}

onBeforeMount(async () => {
	// Без await: тариф и остатки нужны к моменту первого клика, задерживать рендер незачем.
	// Только internal + b2c — в остальных сборках тарифов нет, лишние запросы не нужны.
	if (AppConfig.internalB2C) {
		// Остатки спрашиваем и у гостя: бэк отдаёт ему plan: "guest" со своим лимитом.
		// Заказ мог остаться незавершённым: сначала срез (в нём приходит pending_order),
		// потом попытка подхватить оплату.
		usageStore.fetchUsage().then(() => {
			billingStore.restore()
			maybeShowSuspended()
		})
		// Цена, срок и лимиты тарифа: нужны онбордингу и пейволлу. Без ответа ручки
		// экраны работают на PRO_PLAN_FALLBACK, поэтому запрос ничего не блокирует.
		plansStore.ensurePlans()
		if (authStore.isAuthenticated) profileStore.ensureProfile()
	}
	await commonStore.fetchTopics()
	cookiesStore.checkAndShowCookiesDialog()
})

onMounted(() => {
	window.addEventListener('beforeinstallprompt', (event) => {
		// Не даём браузеру показать свой mini-infobar — показываем собственный pop-up.
		event.preventDefault()
		pwaStore.setDeferredPrompt(event)
		pwaStore.maybeShow(authStore.isAuthenticated)
	})
	window.addEventListener('appinstalled', () => {
		pwaStore.markInstalled()
	})
	// Покрывает iOS (нет beforeinstallprompt) и случай, когда событие пришло до монтирования.
	pwaStore.maybeShow(authStore.isAuthenticated)
	window.addEventListener('online', handleOnline)
	document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
	window.removeEventListener('online', handleOnline)
	document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// Показываем предложение установки только после того, как cookie-баннер закрыт/принят.
watch(
	() => cookiesStore.showCookiesDialog,
	(visible) => {
		if (visible) return
		pwaStore.maybeShow(authStore.isAuthenticated)
		maybeShowSuspended()
	}
)
const route = useRoute()
const loading = computed(() => commonStore.loading)
const sendingMessage = computed(() => commonStore.sendingMessage)
const hideApp = computed(() => commonStore.hideApp)
const internetErrorDialog = computed(() => commonStore.internetErrorDialog)
const infoPage = computed(() => route.meta.infoPage)
const smallPadding = computed(() => route.meta.smallPadding)
const showCookiesDialog = computed(() => cookiesStore.showCookiesDialog)
const showCookiesSetting = computed(() => cookiesStore.showCookiesSettings)
const showInstallPrompt = computed(() => pwaStore.showInstallPrompt)
const showLoginModal = computed(() => commonStore.loginModal)
const showPaywall = computed(() => paywallStore.visible)
const paywallVariant = computed(() => paywallStore.variant)
const paywallTopicName = computed(() => paywallStore.topicName)
const showLimitDialog = computed(() => paywallStore.limitVisible)
const showSuspendedDialog = computed(() => paywallStore.suspendedVisible)

// Платный контур целиком висит на этом флаге: на сборках без него шторки оплаты
// не монтируются, а SDK виджета не подтягивается.
const billingEnabled = AppConfig.internalB2C
const limitResource = computed(() => paywallStore.limitResource)

function closePaywall() {
	paywallStore.close()
}

function closeLimitDialog() {
	paywallStore.closeLimit()
}

function closeSuspendedDialog() {
	paywallStore.closeSuspended()
}
</script>
